/* eslint-disable no-restricted-imports */
import {
  InputAdornment,
  InputBaseComponentProps,
  styled,
  TextField as TextFieldMUI,
} from '@mui/material';

import { ReactNode, useState } from 'react';
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';

import { Visibility, VisibilityOff } from '@mui/icons-material';

import { useI18n } from '@app-velocity/i18n';

import { CopyIcon } from '@app-velocity/icons';

export type TextFieldProps<T extends FieldValues> = UseControllerProps<T> & {
  onFocus?: () => void;
  onBlur?: () => void;
  helperText?: string;
  placeholder?: string;
  error?: boolean;
  fullWidth?: boolean;
  inputProps?: InputBaseComponentProps;
  type?: 'password' | 'text' | 'number';
  size?: 'small' | 'medium' | 'large';
  variant?: 'filled' | 'outlined' | 'filled-outlined';
  multiline?: boolean;
  rows?: number;
  copyable?: boolean;
  className?: string;
  maxRows?: number;
  minRows?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  endAdornment?: ReactNode;
};

const StyledCopyButton = styled('button')({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
});

const StyledVisibilityButton = styled('button')({
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  color: 'var(--text-text-secondary-color)',
});

const StyledTextField = styled(TextFieldMUI)<{ additionalOutline: boolean }>(
  ({ additionalOutline, theme }) => ({
    '.MuiInputBase-root': {
      borderRadius: additionalOutline ? theme.spacing(3) : undefined,
    },
    'textarea, input': {
      borderRadius: additionalOutline ? theme.spacing(3) : undefined,
      border: additionalOutline
        ? '1px solid var(--stroke-stroke-primary-color)'
        : undefined,
    },
  })
);

export const TextField = <T extends FieldValues>({
  name,
  size,
  control,
  rules,
  shouldUnregister,
  helperText,
  type,
  placeholder,
  error,
  inputProps,
  fullWidth,
  defaultValue,
  disabled,
  multiline,
  copyable,
  onFocus,
  onBlur,
  className,
  maxRows,
  minRows,
  onKeyDown,
  endAdornment,
  variant = 'filled',
}: Omit<TextFieldProps<T>, 'label'>) => {
  const { t } = useI18n();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const isPassword = type === 'password';

  // @TODO - verify if copying works after publishing plugin
  const onCopyClick = (value: string) => {
    navigator.clipboard.writeText(value);
  };

  const getEndAdornment = (value: string) => {
    if (copyable) {
      return (
        <InputAdornment position="end" sx={{ position: 'absolute', right: 14 }}>
          <StyledCopyButton onClick={() => onCopyClick(value)}>
            <CopyIcon />
          </StyledCopyButton>
        </InputAdornment>
      );
    }

    if (isPassword) {
      return (
        <InputAdornment position="end" sx={{ position: 'absolute', right: 14 }}>
          <StyledVisibilityButton
            type="button"
            aria-label={
              isPasswordVisible ? t('ui:hide_password') : t('ui:show_password')
            }
            onClick={() => setIsPasswordVisible((visible) => !visible)}
          >
            {isPasswordVisible ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            )}
          </StyledVisibilityButton>
        </InputAdornment>
      );
    }

    if (endAdornment) {
      return (
        <InputAdornment position="end" sx={{ position: 'absolute', right: 14 }}>
          {endAdornment}
        </InputAdornment>
      );
    }

    return undefined;
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      disabled={disabled}
      render={({ field, fieldState }) => (
        <StyledTextField
          {...field}
          additionalOutline={variant === 'filled-outlined'}
          className={className}
          disabled={disabled}
          error={error ?? !!fieldState.error}
          type={isPassword && isPasswordVisible ? 'text' : type}
          fullWidth={fullWidth}
          helperText={helperText ?? fieldState.error?.message}
          placeholder={placeholder}
          inputProps={inputProps}
          size={size}
          value={field.value}
          multiline={multiline}
          defaultValue={defaultValue}
          variant={variant === 'filled-outlined' ? 'filled' : variant}
          onFocus={onFocus}
          onBlur={() => {
            field.onBlur();
            onBlur?.();
          }}
          maxRows={maxRows}
          minRows={minRows}
          onKeyDown={onKeyDown}
          InputProps={{
            endAdornment: getEndAdornment(field.value || defaultValue || ''),
          }}
        />
      )}
    />
  );
};
